import { Injectable } from "@angular/core";
import * as moment from "moment";
import { timer } from "rxjs";
import { ApplicationService } from "src/app/services/application.service";
import { environment } from "src/environments/environment";

// Extend Window interface to include Salesforce embedded service
declare global {
  interface Window {
    embedded_svc: any;
    showChatBubble: () => void;
    showChatWindow: () => void;
    sendMessage: (message: string) => void;
    setDepartment: (companyId: string, partType: string) => void;
  }
}

interface SalesforceChatConfig {
  salesforceUrl: string;
  communityUrl: string;
  organizationId: string;
  deploymentName: string;
  liveAgentContentURL: string;
  deploymentId: string;
  defaultButtonId: string;
  liveAgentURL: string;
  eswLiveAgentDevName: string;
  buttonMapping: {
    [key: string]: string;
  };
}

@Injectable({
  providedIn: "root",
})
export class ChatService {
  loaded = false;
  private scriptLoaded = false;
  
  private config: SalesforceChatConfig = {
    salesforceUrl: 'https://accell.my.salesforce.com',
    communityUrl: 'https://accelllivechat.secure.force.com',
    organizationId: '00D0X000000Oixf',
    deploymentName: 'B2B_Benelux_Accentry',
    liveAgentContentURL: 'https://c.la3-c2-cdg.salesforceliveagent.com/content',
    deploymentId: '57267000000PPXa',
    defaultButtonId: '57367000000PQAc',
    liveAgentURL: 'https://d.la3-c2-cdg.salesforceliveagent.com/chat',
    eswLiveAgentDevName: 'EmbeddedServiceLiveAgent_Parent04I67000000TNOHEA4_175ff3a81d8',
    buttonMapping: {
      'EBSC': '57367000000PQAa',
      'Sales': '57367000000PQAd',
      'Juncker': '57367000000PQAb',
      'Parts Accell NL': '57367000000PQAc'
    }
  };

  constructor(private applicationService: ApplicationService) {
    // Suppress zone.js warnings from Salesforce chat
  }

  /**
   * Initialize Salesforce chat functionality
   */
  initializeSalesforceChat(): void {
    const accountCode = localStorage.getItem("accountCode");
    const chatDisabled = localStorage.getItem("chat_disabled");
    
    if (!accountCode || chatDisabled !== null) {
      return;
    }

    if (!this.loaded) {
      this.loaded = true;
      setTimeout(() => {
        this.loadSalesforceScript();
      }, 1000);
    }
  }

  /**
   * Load Salesforce embedded service script dynamically
   */
  private loadSalesforceScript(): void {
    if (this.scriptLoaded) {
      this.initESW('https://service.force.com');
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('src', 'https://service.force.com/embeddedservice/5.0/esw.min.js');
    script.onload = () => {
      this.scriptLoaded = true;
      this.initESW(null);
    };
    document.body.appendChild(script);
  }

  /**
   * Initialize Embedded Service Widget
   */
  private initESW(gslbBaseURL: string | null): void {
    if (typeof window.embedded_svc === 'undefined') {
      return;
    }

    const language = this.getFormattedLanguage();
    const userInfo = this.getUserInfo();
    const fieldLabels = this.getFieldLabels(language);

    // Configure embedded service settings
    window.embedded_svc.settings.displayHelpButton = true;
    window.embedded_svc.settings.language = language;
    window.embedded_svc.settings.defaultMinimizedText = 'Chat';
    window.embedded_svc.settings.disabledMinimizedText = 'Offline';
    
    // Button routing based on department
    window.embedded_svc.settings.directToButtonRouting = (prechatFormData: any) => {
      const department = prechatFormData[3]?.value;
      return this.config.buttonMapping[department] || this.config.buttonMapping['Sales'];
    };

    // Enable LiveAgent
    window.embedded_svc.settings.enabledFeatures = ['LiveAgent'];
    window.embedded_svc.settings.entryFeature = 'LiveAgent';

    // Prepopulate form fields
    window.embedded_svc.settings.prepopulatedPrechatFields = {
      FirstName: userInfo.firstName,
      LastName: userInfo.lastName,
      Customer_Number__c: userInfo.customerNumber,
      Question__c: ' ',
      Page__c: '',
      Department__c: '-'
    };

    // Extra prechat info for Salesforce entity mapping
    window.embedded_svc.settings.extraPrechatInfo = [
      {
        entityName: "Account",
        showOnCreate: true,
        linkToEntityName: "Case",
        linkToEntityField: "AccountId",
        saveToTranscript: "AccountId",
        entityFieldMaps: [{
          isExactMatch: true,
          fieldName: "Accentry_Id__c",
          doCreate: false,
          doFind: true,
          label: fieldLabels.customerNumber
        }]
      },
      {
        entityFieldMaps: [
          {
            doCreate: false,
            doFind: false,
            fieldName: "LastName",
            isExactMatch: true,
            label: fieldLabels.lastName
          },
          {
            doCreate: false,
            doFind: false,
            fieldName: "FirstName",
            isExactMatch: true,
            label: fieldLabels.firstName
          }
        ],
        entityName: "Contact"
      }
    ];

    // Initialize embedded service
    window.embedded_svc.init(
      this.config.salesforceUrl,
      this.config.communityUrl,
      gslbBaseURL,
      this.config.organizationId,
      this.config.deploymentName,
      {
        baseLiveAgentContentURL: this.config.liveAgentContentURL,
        deploymentId: this.config.deploymentId,
        buttonId: this.config.defaultButtonId,
        baseLiveAgentURL: this.config.liveAgentURL,
        eswLiveAgentDevName: this.config.eswLiveAgentDevName,
        isOfflineSupportEnabled: false
      }
    );
  }

  /**
   * Get formatted language for Salesforce
   */
  private getFormattedLanguage(): string {
    const language = localStorage.getItem("language")?.toLowerCase() || "en";
    
    const languageMap: { [key: string]: string } = {
      'nl': 'nl_NL',
      'en': 'en_US'
    };
    
    return languageMap[language] || language;
  }

  /**
   * Get user information from localStorage
   */
  private getUserInfo(): { firstName: string; lastName: string; customerNumber: string } {
    return {
      firstName: '',
      lastName: localStorage.getItem("userName") || '',
      customerNumber: localStorage.getItem("accountCode") || ''
    };
  }

  /**
   * Get field labels based on language
   */
  private getFieldLabels(language: string): { customerNumber: string; firstName: string; lastName: string } {
    const labels: { [key: string]: any } = {
      'nl_NL': {
        customerNumber: 'Klantnr.',
        firstName: 'Voornaam',
        lastName: 'Achternaam'
      },
      'fr': {
        customerNumber: 'Numéro client',
        firstName: 'Prénom',
        lastName: 'Nom'
      },
      'de': {
        customerNumber: 'Kundennummer',
        firstName: 'Vorname',
        lastName: 'Nachname'
      },
      'es': {
        customerNumber: 'Número de cliente',
        firstName: 'Nombre',
        lastName: 'Apellidos'
      },
      'it': {
        customerNumber: 'Numero cliente',
        firstName: 'Nome',
        lastName: 'Cognome'
      },
      'en_US': {
        customerNumber: 'Customer number',
        firstName: 'First Name',
        lastName: 'Last Name'
      },
      'da': {
        customerNumber: 'Kundenummer.',
        firstName: 'Fornavn',
        lastName: 'Efternavn'
      }
    };

    return labels[language] || labels['en_US'];
  }

  /**
   * Suppress zone.js warnings from Salesforce chat
   */
  private suppressWarnings(): void {
    const originalWarn = console.warn;
    console.warn = function(...args: any[]) {
      const warningMessage = typeof args[0] === 'string' ? args[0] : '';

      if (
        warningMessage.includes('Embedded Service Chat') ||
        warningMessage.includes('No client method with the name') ||
        warningMessage.includes('NG0956:') ||
        warningMessage.includes('Deprecation warning: value provided is not in a recognized RFC2822 or ISO format.')
      ) {
        return;
      }
      originalWarn.apply(console, args);
    };
  }

  // Legacy methods for backward compatibility
  initChat() {
    // if (
    //   this.applicationService.getSelectedLanguage() === "NL" &&
    //   this.applicationService.getSelectCompanyGroupCode() === "ANL"
    // ) {
    //   this.initializeSalesforceChat();
    //   return true;
    // } else {
    //   this.toggleChat(false);
    //   return false;
    // }
    return false
  }

  showChat() {
    if (window.showChatWindow) {
      window.showChatWindow();
    }
  }

  hasChat() {
    const el = document.getElementById("chatButton");
    if (el && el.classList) {
      const isOnline = el.classList.contains("online");
      if (isOnline) {
        return true;
      }
    }
    return false;
  }

  sendMessage(message: string) {
    if (window.sendMessage) {
      window.sendMessage(message);
    }
  }

  setDepartment(companyId: string, partType: string) {
    if (window.setDepartment) {
      window.setDepartment(companyId, partType);
    }
  }

  toggleChat(state: boolean) {
    const chatButtonsQuery = document.querySelectorAll(".embeddedServiceHelpButton");
    if (chatButtonsQuery && chatButtonsQuery.length) {
      const chatButton = chatButtonsQuery[0] as HTMLDivElement;
      chatButton.style.display = state ? "block" : "none";
    }
  }
}
